import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import axiosInstance from "../../utils/axiosInstance";
import { BASE_URL } from "../../utils/BASE_URL";
import toast from "react-hot-toast";

const initialState = {
    presets: [],
    activeTheme: null,
    loading: false,
    error: null,
};

// Fetch all presets
export const fetchPresets = createAsyncThunk(
    'siteConfig/fetchPresets',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(`${BASE_URL}/colors`);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Fetch Active Theme
export const fetchActiveTheme = createAsyncThunk(
    'siteConfig/fetchActiveTheme',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(`${BASE_URL}/site-config/active`);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Save Active Theme
export const saveActiveTheme = createAsyncThunk(
    'siteConfig/saveActiveTheme',
    async ({ id, ...colors }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            Object.entries(colors).forEach(([key, value]) => {
                formData.append(key, value);
            });
            const { data } = await axios.put(`${BASE_URL}/colors/${id}`, formData);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Add a new preset
export const addPreset = createAsyncThunk(
    'siteConfig/addPreset',
    async (presetData, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            Object.entries(presetData).forEach(([key, value]) => {
                formData.append(key, value);
            });
            const { data } = await axiosInstance.post(`${BASE_URL}/colors`, formData);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Update a preset
export const updatePreset = createAsyncThunk(
    'siteConfig/updatePreset',
    async ({ id, ...presetData }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            Object.entries(presetData).forEach(([key, value]) => {
                formData.append(key, value);
            });
            const { data } = await axios.put(`${BASE_URL}/colors/${id}`, formData);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Delete a preset
export const deletePreset = createAsyncThunk(
    'siteConfig/deletePreset',
    async (id, { rejectWithValue }) => {
        try {
            await axios.delete(`${BASE_URL}/colors/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const siteConfigSlice = createSlice({
    name: 'siteConfig',
    initialState,
    reducers: {
        applyPresetLocally: (state, action) => {
            // This is just a helper for local preview, doesn't persist state
            const root = document.documentElement;
            Object.entries(action.payload).forEach(([key, value]) => {
                root.style.setProperty(`--color-${key}`, value);
            });
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Presets
            .addCase(fetchPresets.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPresets.fulfilled, (state, action) => {
                state.loading = false;
                state.presets = action.payload;
            })
            .addCase(fetchPresets.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch Active Theme
            .addCase(fetchActiveTheme.fulfilled, (state, action) => {
                state.activeTheme = action.payload;
            })
            // Save Active Theme
            .addCase(saveActiveTheme.pending, (state) => {
                state.loading = true;
            })
            .addCase(saveActiveTheme.fulfilled, (state, action) => {
                state.loading = false;
                state.activeTheme = action.payload;
                toast.success("Theme configuration saved and applied!");
            })
            .addCase(saveActiveTheme.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                toast.error(`Failed to save theme: ${action.payload}`);
            })
            // Add Preset
            .addCase(addPreset.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addPreset.fulfilled, (state, action) => {
                state.loading = false;
                state.presets.unshift(action.payload); // Add new preset to the beginning
                toast.success("Preset saved successfully!");
            })
            .addCase(addPreset.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                toast.error(`Failed to save preset: ${action.payload}`);
            })
            // Update Preset
            .addCase(updatePreset.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updatePreset.fulfilled, (state, action) => {
                state.loading = false;
                state.presets = state.presets.map(p =>
                    p._id === action.payload._id ? action.payload : p
                );
                toast.success("Preset updated successfully!");
            })
            .addCase(updatePreset.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                toast.error(`Failed to update preset: ${action.payload}`);
            })
            // Delete Preset
            .addCase(deletePreset.fulfilled, (state, action) => {
                state.presets = state.presets.filter(p => p._id !== action.payload);
                toast.success("Preset deleted successfully!");
            })
            .addCase(deletePreset.rejected, (state, action) => {
                toast.error(`Failed to delete preset: ${action.payload}`);
            });
    }
});

export const { applyPresetLocally } = siteConfigSlice.actions;
export default siteConfigSlice.reducer;
